import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { CrearItemServicio } from "../../utils/CrearItemServicio";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { getAuthData } from "../../utils/auth";

const apiUrl = process.env.REACT_APP_API_URL;

const QUOTA_OPTIONS = [
  { label: 3, key: "cuota_1" },
  { label: 6, key: "cuota_2" },
  { label: 9, key: "cuota_3" },
  { label: 12, key: "cuota_4" },
  { label: 18, key: "cuota_5" },
];

const Parcelas = ({ agregarAlPresupuesto, resetTrigger, deshabilitarBoton }) => {
  const [parcelaData, setParcelaData] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [errorCodigo, setErrorCodigo] = useState(false);
  const [cuotas, setCuotas] = useState(3);
  const [precioPlan, setPrecioPlan] = useState(0);
  const [monto_por_cuota, setMontoCuotas] = useState(0);
  const [modalidad, setModalidad] = useState("");
  const [metodoPago, setMetodoPago] = useState("");
  const [contadoChecked, setContadoChecked] = useState(false);
  const [mapaUrl, setMapaUrl] = useState(null);

  const auth = getAuthData();
  const token = auth?.token;
  const vendedor_id = auth?.vendedorId;

  // Derived state for available quotas
  const availableQuotas = React.useMemo(() => {
    if (!planSeleccionado) return [];
    return QUOTA_OPTIONS.filter((option) => {
      const val = planSeleccionado[option.key];
      return val !== null && val !== undefined && parseFloat(val) > 0;
    });
  }, [planSeleccionado]);

  // Effect to ensure valid quota selection
  useEffect(() => {
    if (modalidad === "financiado" && availableQuotas.length > 0) {
      const isCurrentValid = availableQuotas.some((q) => q.label === cuotas);
      if (!isCurrentValid) {
        setCuotas(availableQuotas[0].label);
      }
    }
  }, [modalidad, availableQuotas, cuotas]);

  const precioContado = planSeleccionado ? planSeleccionado.de_contado || 0 : 0;
  const precioContado_bcv = planSeleccionado ? planSeleccionado.de_contado_bcv || 0 : 0;

  const disponible = parcelaData ? parcelaData.disponible || 0 : 0;
  const puestos = planSeleccionado
    ? parseInt(planSeleccionado.puestos) || 0
    : 0;


  useEffect(() => {
    let objectUrl = null;

    const fetchMapa = async () => {
      try {

        const response = await axios.get(
          `${apiUrl}/api/v1/mapa/Parcelas.jpg`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            responseType: "blob"
          }
        );

        objectUrl = URL.createObjectURL(response.data);
        setMapaUrl(objectUrl);

      } catch (error) {
        console.error("Error al obtener el mapa:", error);
        toast.error("No se pudo cargar el mapa.");
      }
    };

    if (token) {
      fetchMapa();
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };

  }, [token, apiUrl]);

  const openImageInNewTab = () => {

    if (!mapaUrl) {
      toast.error("El mapa no está disponible en este momento.");
      return;
    }

    window.open(mapaUrl, "_blank");
  };

  useEffect(() => {
    const fetchPlanes = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/api/planes/productos?tipo_servicio=parcela`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPlanes(response.data);
      } catch (error) {
        console.error("Error al obtener los planes:", error);
        toast.error("Error al cargar los planes de parcelas.");
      }
    };
    fetchPlanes();
  }, []);

  const handleCodigoChange = (e) => {
    const input = e.target.value.toUpperCase();
    setCodigo(input);
    setErrorCodigo(false);
    setParcelaData(null);
  };

  const handleCodigoBlur = async () => {
    if (!codigo) {
      setErrorCodigo(true);
      toast.error("Por favor, ingrese el código de la parcela.");
      return;
    }

    try {
      const normalizedCode = codigo.replace(/\s/g, "").toUpperCase();
      let finalCode = normalizedCode;

      if (normalizedCode.match(/^A\d{2}-\d{2}$/)) {
        finalCode = `${normalizedCode}-01`;
      }

      if (!finalCode.match(/^A\d{2}-\d{2}-\d{2}$/)) {
        toast.error(
          "El código debe tener el formato AXX-XX-XX (ejemplo: A03-26-01)."
        );
        setErrorCodigo(true);
        setCodigo("");
        return;
      }

      const response = await axios.get(
        `${apiUrl}/api/consulta-parcela/${finalCode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const parcela = response.data;
      setParcelaData(parcela);

      if (parcela.disponible === 0) {
        toast.error(`La parcela ${finalCode} no tiene puestos disponibles.`);
        setCodigo("");
      } else {
        toast.success(
          `La parcela ${finalCode} tiene ${parcela.disponible} puesto(s) disponible(s).`
        );
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.warning(
          `No se encontró la parcela con código ${codigo}. Puede crear una nueva entrada.`
        );
        setParcelaData({
          estado: "disponible",
          disponible: 3,
          precio_contado: 0,
          precio_financiado: 0,
        });
      } else {
        console.error("Error al consultar la parcela:", error);
        toast.error("Error al verificar el código de la parcela.");
      }
    }
  };

  useEffect(() => {
    setPlanSeleccionado(null);
    setModalidad("");
    setCuotas(3);
    setMontoCuotas(0);
    setMetodoPago("");
    setPrecioPlan(0);
    setContadoChecked(false);
  }, [resetTrigger]);

  const handlePlanChange = (e) => {
    const planId = e.target.value;
    const plan = planes.find((p) => p.id === planId);
    setPlanSeleccionado(plan);
    setPrecioPlan(0);
    setModalidad("");
    setCuotas(3);
    setMontoCuotas(0);
  };

  const handleModalidadChange = (e) => {
    const val = e.target.value;
    setModalidad(val);

    setPrecioPlan(0);
    setCuotas(3);
    setMontoCuotas(0);
    // Solo marcamos divisas por defecto al cambiar a De Contado
    if (val === "contado") {
      setContadoChecked(true);
    } else {
      setContadoChecked(false);
    }
  };

  // Calcular montos
  useEffect(() => {
    if (!planSeleccionado || !modalidad) {
      setPrecioPlan(0);
      setMontoCuotas(0);
      return;
    }

    if (modalidad === "contado") {
      const precio = contadoChecked ? precioContado : precioContado_bcv;
      const metodo = contadoChecked ? "USD" : "VES";

      setMetodoPago(metodo);
      setPrecioPlan(precio);
      setMontoCuotas(0);
    }

    if (modalidad === "financiado") {
      const selectedQuotaOption = QUOTA_OPTIONS.find((q) => q.label === cuotas);

      // En este caso, el valor en cuota_X representa el PRECIO TOTAL del plan financiado a X meses
      const precioTotalFinanciado = selectedQuotaOption && planSeleccionado
        ? parseFloat(planSeleccionado[selectedQuotaOption.key]) || 0
        : 0;

      // La cuota mensual se calcula dividiendo ese total entre el número de meses
      const cuotaMensual = cuotas > 0 ? precioTotalFinanciado / cuotas : 0;

      setMontoCuotas(cuotaMensual);
      setMetodoPago("USD");
      setPrecioPlan(precioTotalFinanciado);
    }
  }, [modalidad, cuotas, planSeleccionado, contadoChecked]);

  const handleAgregar = () => {
    if (!codigo || !parcelaData || !planSeleccionado || !modalidad) {
      toast.error("Por favor, complete todos los campos requeridos.");
      return;
    }

    if (disponible === 0 || puestos > disponible) {
      toast.error(
        "No se puede agregar una parcela que no esté disponible o exceda los puestos."
      );
      return;
    }

    const item = CrearItemServicio({
      tipo_servicio: "parcelas",
      tipo_plan: "producto",
      descripcion: planSeleccionado.descripcion,
      modalidad_pago: modalidad === "contado" ? "contado" : "financiado",
      codigo,
      metodo_pago: metodoPago,
      numero_cuotas:
        modalidad === "financiado" ? cuotas : null,
      monto_por_cuota:
        modalidad === "financiado" ? monto_por_cuota : null,
      precio_plan: precioPlan,

      detalles_adicionales: {
        codigo,
        puestos: puestos,
        disponible: disponible,
      },
    });

    agregarAlPresupuesto(item);
  };

  return (
    <>
      <Typography
        sx={{
          marginBottom: "5px",
          fontFamily: "Bakbak One, sans-serif",
          color: "#0A4575",
          textAlign: "center",
        }}
        variant="h5"
      >
        SOLICITUD DE CONTRATO DE PARCELAS
      </Typography>
      <Typography
        sx={{
          marginTop: "15px",
          marginBottom: "10px",
          fontFamily: "Bakbak One, sans-serif",
          color: "#0A4575",
        }}
        variant="h6"
      >
        PLAN A SOLICITAR
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <Tooltip
            title="Por favor, ingrese el código de la parcela."
            open={errorCodigo}
            disableHoverListener
            placement="top"
            arrow
          >
            <TextField
              fullWidth
              label="Código Parcela"
              variant="outlined"
              size="small"
              value={codigo}
              onChange={handleCodigoChange}
              onBlur={handleCodigoBlur}
              sx={{ boxShadow: 3 }}
              error={errorCodigo}
            />
          </Tooltip>
        </Grid>

        <Grid sx={{ mt: 2 }}>

          <IconButton onClick={openImageInNewTab}>
            <HelpOutlineIcon />
          </IconButton>
        </Grid>
      </Grid>{" "}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {disponible > 0 && (
          <Grid item xs={3}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              sx={{ boxShadow: 3 }}
            >
              <InputLabel>Seleccione el Plan</InputLabel>
              <Select
                label="Seleccione el Plan"
                value={planSeleccionado ? planSeleccionado.id : ""}
                onChange={handlePlanChange}
                autoComplete="off"
              >
                <MenuItem value="" disabled>
                  Seleccione
                </MenuItem>
                {planes
                  .filter(
                    (plan) => plan.is_active === true || plan.is_active === 1
                  )
                  .map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.descripcion}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {disponible > 0 && planSeleccionado && (
          <Grid item xs={2}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              sx={{ boxShadow: 3 }}
            >
              <InputLabel id="modalidad-label">Modalidad</InputLabel>
              <Select
                labelId="modalidad-label"
                id="modalidad"
                label="Modalidad"
                value={modalidad}
                onChange={handleModalidadChange}
                autoComplete="off"
              >
                <MenuItem value="" disabled>
                  Seleccione
                </MenuItem>
                <MenuItem value="contado">De Contado</MenuItem>
                <MenuItem value="financiado">Financiado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {disponible > 0 && planSeleccionado && modalidad && (
          <>
            {modalidad === "contado" && (
              <>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Precio del Plan"
                    variant="outlined"
                    size="small"
                    value={`$${precioPlan}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAgregar}
                    disabled={disponible === 0 || puestos > disponible || deshabilitarBoton}
                    fullWidth
                  >
                    Crear Solicitud
                  </Button>
                </Grid>
              </>
            )}

            {modalidad === "financiado" && (
              <>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Precio del Plan"
                    variant="outlined"
                    size="small"
                    value={`$${precioPlan}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ boxShadow: 3 }}
                  >
                    <InputLabel id="cuotas-label">Cuotas</InputLabel>
                    <Select
                      labelId="cuotas-label"
                      id="cuotas"
                      label="Cuotas"
                      value={cuotas}
                      onChange={(e) =>
                        setCuotas(
                          parseInt(e.target.value) ||
                          (availableQuotas.length > 0
                            ? availableQuotas[0].label
                            : 3)
                        )
                      }
                      autoComplete="off"
                    >
                      {availableQuotas.map((option) => (
                        <MenuItem key={option.label} value={option.label}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Monto por Cuota"
                    variant="outlined"
                    size="small"
                    value={`$${monto_por_cuota.toFixed(2)}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAgregar}
                    disabled={disponible === 0 || puestos > disponible || deshabilitarBoton}
                    fullWidth
                  >
                    Crear Solicitud
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </>
  );
};

export default Parcelas;
